

import { IconTag } from '@tabler/icons-react'
import { App, Avatar, Card, Flex, Typography } from 'antd/es'
import dayjs from 'dayjs'
import React, { useCallback } from 'react'

import MediaView from '@/components/shared/media-view'
import MoreOptionsModal from '@/components/shared/more-options-modal'
import { MediaType } from '@/model/media'
import { convertPostResponseToMedias, PostResponse } from '@/model/post'
import { deletePost } from '@/redux/post-actions'
import { getPostLink } from '@/redux/send-post'
import { useAppDispatch } from '@/redux/utils'
import { getBaeApiBaseUrl } from '@/utils/env'
import { formatPrice } from '@/utils/formatter'
import htmlToText from '@/utils/html-to-text'

import s from './post-card.module.css'
import PostImage from './post-images'


const { Title } = Typography

export interface PostCardProps {
  creatorId: number
  post: PostResponse
}

const formatDate = (date: string) => {
  return dayjs(date).format('MMM D, YYYY h:mm A')
}

export function PostCard({ post, creatorId }: PostCardProps) {
  const { message, modal } = App.useApp()
  const dispatch = useAppDispatch()

  const handleSharePost = useCallback(async () => {
    const link = await dispatch(
      getPostLink({ creatorId, uid: post.user.uid, pid: post.post.id }),
    ).unwrap()

    if (!link?.ref) {
      void message.error('Failed to get the link, please try again later')
      return
    }

    void navigator.clipboard
      .writeText(`${getBaeApiBaseUrl()}/link/${link.ref}`)
      .then(() => void message.success('Link copied to clipboard'))
      .catch((err) => console.error('Failed to copy the link: ', err))
  }, [creatorId, dispatch, message, post.post.id, post.user.uid])

  const handleDeletePost = useCallback(() => {
    void modal.confirm({
      title: 'Delete Post',
      content: 'Are you sure you want to delete this post?',
      centered: true,
      okType: 'danger',
      okText: 'Delete',
      icon: null,
      maskClosable: true,
      onOk: async () => {
        await dispatch(deletePost({
          creatorId,
          uid: post.user.uid,
          pid: post.post.id,
        })).unwrap()
        void message.success('Post deleted successfully')
      },
    })
  }, [creatorId, dispatch, message, modal, post.post.id, post.user.uid])

  return (
    <Card
      className={s.PostCard}
      title={
        <Flex justify="space-between" align="center">
          <Flex gap={8} align="center">
            <Avatar size="large" src={post.user.avatar} />
            <Title level={3}>{post.user.username}</Title>
          </Flex>
          <Flex align="center" gap={16} style={{ fontSize: 14 }}>
            <Flex gap={8} align="end" vertical>
              <span>{formatDate(post.post.created_at)}</span>
              <Flex align="center" gap={4}>
                <IconTag />
                <span>Price {formatPrice()(post.post.price)}</span>
              </Flex>
            </Flex>
            <Flex>
              <MoreOptionsModal
                type="menu"
                options={[
                  { name: 'Copy link to share', onClick: handleSharePost },
                  { name: 'Delete Post', onClick: handleDeletePost },
                ]}
              />
            </Flex>
          </Flex>
        </Flex>
      }
    >
      <Flex vertical>
        {/* TODO: combine into media view */}
        {post.post.type === MediaType.Video ? (
          <MediaView className={s.VideoShow} media={convertPostResponseToMedias(post)[0]} />
        ) : (
          <PostImage listItem={post} />
        )}
      </Flex>
      <Flex>
        <Title level={4}>{htmlToText(post.post.title)}</Title>
      </Flex>
    </Card>
  )
}
